import CModal from "@/shared/components/wrappers/modal/CModal.vue";
import { addCategoryIcon, addIcon, sortIcon } from "common/defaultIcons";
import FaqCategoryDialog from "components/knowledge-base/faq/FaqCategoryDialog.vue";
import FaqSortDialog from "components/knowledge-base/faq/FaqSortDialog.vue";
import type {
  FaqCategoryPayload,
  FaqCategoryWithItems,
} from "models/knowledge-base/faq";
import { storeToRefs } from "pinia";
import { useQuasar } from "quasar";
import { useFaqStore } from "store/modules/faqStore";
import { useRouter } from "vue-router";

interface CategoryMenuItem {
  label: string;
  icon: string;
  onClick: () => void;
}

interface AxiosLikeError {
  response?: {
    data?: {
      errors?: { category?: string[] };
      message?: string;
    };
  };
}

export const useFaqCategoryActions = () => {
  const $q = useQuasar();
  const router = useRouter();
  const faqStore = useFaqStore();
  const { categories } = storeToRefs(faqStore);
  const { createCategory, updateCategory, deleteCategory } = faqStore;

  const findCategory = (uuid: string): FaqCategoryWithItems | null =>
    categories.value.find((c) => c.uuid === uuid) ?? null;

  const openSortDialog = (): void => {
    $q.dialog({
      component: FaqSortDialog,
      componentProps: {
        categories: categories.value,
      },
    });
  };

  const openCreateDialog = (): void => {
    $q.dialog({
      component: FaqCategoryDialog,
    }).onOk(async (payload: FaqCategoryPayload) => {
      await createCategory(payload);
    });
  };

  const openEditDialog = (uuid: string): void => {
    const category = findCategory(uuid);
    if (!category) {
      return;
    }
    $q.dialog({
      component: FaqCategoryDialog,
      componentProps: { category },
    }).onOk(async (payload: FaqCategoryPayload) => {
      await updateCategory(uuid, payload);
    });
  };

  const showDeleteErrorDialog = (message: string): void => {
    $q.dialog({
      component: CModal,
      componentProps: {
        title: "Verwijderen mislukt",
        message,
        type: "negative",
        cancel: false,
        primaryLabel: "Ok",
      },
    });
  };

  const openDeleteDialog = (uuid: string): void => {
    const category = findCategory(uuid);
    if (!category) {
      return;
    }
    const itemCount = category.items?.length ?? 0;
    if (itemCount > 0) {
      $q.dialog({
        component: CModal,
        componentProps: {
          title: "Categorie bevat vragen",
          message: `Deze categorie bevat nog ${itemCount} vragen. Verplaats of verwijder de vragen eerst voordat je de categorie verwijdert.`,
          type: "warning",
          cancel: false,
          primaryLabel: "Ok",
        },
      });
      return;
    }
    $q.dialog({
      component: CModal,
      componentProps: {
        title: "Categorie verwijderen",
        message: `Weet je zeker dat je de categorie "${category.title}" wilt verwijderen?`,
        type: "negative",
        cancel: true,
        primaryLabel: "Verwijderen",
      },
    }).onOk(async () => {
      try {
        await deleteCategory(uuid);
      } catch (e: unknown) {
        const err = e as AxiosLikeError;
        const message =
          err?.response?.data?.errors?.category?.[0] ??
          err?.response?.data?.message ??
          "Kon categorie niet verwijderen";
        showDeleteErrorDialog(message);
      }
    });
  };

  const menuItems: CategoryMenuItem[] = [
    {
      label: "Nieuwe vraag",
      icon: addIcon,
      onClick: () => {
        router.push({ name: "faqItemAdd" });
      },
    },
    {
      label: "Nieuwe categorie",
      icon: addCategoryIcon,
      onClick: openCreateDialog,
    },
    {
      label: "Sorteren",
      icon: sortIcon,
      onClick: openSortDialog,
    },
  ];

  return {
    menuItems,
    openCreateDialog,
    openEditDialog,
    openDeleteDialog,
    openSortDialog,
  };
};
